import { fuzzySearch } from '@flakiness/server/common/fuzzySearch.js';
import { Ranges } from '@flakiness/server/common/ranges.js';
import { SlDropdown, SlInput, SlShowEvent } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { consumeDOMEvent } from '../utils.js';
import { docLinkStyles } from './cssstyles.js';

export type RepositoryItem = {
  // Stable identity for the item, e.g. `owner/name`.
  id: string,
  // Rendered dimmed before the bolded `name`, e.g. `owner/` or `group/subgroup/`.
  prefix: string,
  name: string,
  avatarUrl?: string,
  avatarShape?: 'circle'|'rounded',
  isPrivate?: boolean,
};

/**
 * A searchable dropdown over repositories. Knows nothing about where they came
 * from: callers fetch, map to `RepositoryItem`, and render their own empty and
 * error states around this.
 */
@customElement('pick-repository')
export class PickRepository extends LitElement {
  @property({ attribute: false }) items: RepositoryItem[] = [];
  @property({ attribute: false }) icon?: string;
  @property({ attribute: false }) placeholder?: string;

  @state() private _selected?: RepositoryItem;
  @state() private _filter: string = '';
  @state() private _preselectedIndex: number = 0;
  @query('sl-dropdown') private _dropdown?: SlDropdown;
  @query('sl-input') private _input?: SlInput;

  private _select(item: RepositoryItem) {
    this._selected = item;
    this._dropdown?.hide();
    this.dispatchEvent(new CustomEvent<RepositoryItem>('repository-picked', {
      detail: item,
    }));
  }

  private _filteredItems() {
    return this.items.map(item => {
      const matches = fuzzySearch(item.prefix + item.name, this._filter);
      return [item, matches] as const;
    }).filter(([, matches]) => !this._filter || matches.length > 0);
  }

  private _onKeyDown(e: KeyboardEvent) {
    // Readline-style: Ctrl-n / Ctrl-p alias ArrowDown / ArrowUp. Not Cmd, since
    // Cmd-N would open a new browser window on macOS.
    const key = e.key.toLowerCase();
    const isDown = e.key === 'ArrowDown' || (e.ctrlKey && !e.metaKey && key === 'n');
    const isUp = e.key === 'ArrowUp' || (e.ctrlKey && !e.metaKey && key === 'p');
    const isEnter = e.key === 'Enter';
    if (!isDown && !isUp && !isEnter)
      return;
    consumeDOMEvent(e);
    const filtered = this._filteredItems();
    if (!filtered.length)
      return;
    const clamped = Math.min(this._preselectedIndex, filtered.length - 1);
    if (isDown)
      this._preselectedIndex = Math.min(clamped + 1, filtered.length - 1);
    else if (isUp)
      this._preselectedIndex = Math.max(clamped - 1, 0);
    else
      this._select(filtered[clamped][0]);
  }

  override render() {
    const itemsAndMatches = this._filteredItems();
    const preselectedIndex = Math.min(this._preselectedIndex, itemsAndMatches.length - 1);

    return html`
      <sl-dropdown placement=bottom-start distance=10 @sl-after-show=${(e: SlShowEvent) => {
        // Guard against inner shoelace components (e.g. tooltips) re-firing this event.
        if (e.target === this._dropdown)
          this._input?.focus();
      }}>
        <sl-button variant=${this._selected ? nothing : 'primary'} slot=trigger caret trigger>
          ${this._selected ? html`
            <h-box>
              <sl-avatar image=${this._selected.avatarUrl ?? nothing} shape=${this._selected.avatarShape ?? 'circle'}></sl-avatar>
              <div>${this._selected.prefix}<strong>${this._selected.name}</strong></div>
            </h-box>
          ` : html`
            <h-box>
              <sl-icon name=${this.icon ?? nothing}></sl-icon>
              ${this.placeholder}
            </h-box>
          `}
        </sl-button>
        <section class=dropdown-body>
          <sl-input clearable placeholder="Filter ${this.items.length} projects"
            @sl-input=${() => {
              if (!this._input)
                return;
              this._filter = this._input.value.trim();
              this._preselectedIndex = 0;
            }}
            @keydown=${this._onKeyDown}>
            <sl-icon name="search" slot="prefix"></sl-icon>
          </sl-input>
          <sl-divider></sl-divider>
          ${itemsAndMatches.length ? html`
            <section class=repolist>
              ${itemsAndMatches.map(([item, matches], idx) => this._renderItem(item, matches, idx === preselectedIndex))}
            </section>
          ` : html`
            <v-box style="align-items: center; color: var(--sl-color-neutral-500);">No matches</v-box>
          `}
        </section>
      </sl-dropdown>
    `;
  }

  private _renderItem(item: RepositoryItem, matches: Ranges.Ranges<number>, isPreselected: boolean) {
    const prefixMatches = Ranges.intersect(matches, [0, item.prefix.length - 1] as any);
    const suffixMatches = Ranges.subtract(matches, [0, item.prefix.length - 1] as any).map(q => q - item.prefix.length);

    return html`
      <button class=repo ?preselected=${isPreselected} @click=${this._select.bind(this, item)}>
        <sl-icon name=${this._selected?.id === item.id ? 'check' : nothing}></sl-icon>
        <sl-avatar image=${item.avatarUrl ?? nothing} shape=${item.avatarShape ?? 'circle'}></sl-avatar>
        <h-box class=name>
          <div>
            <hl-span .text=${item.prefix} .hl=${prefixMatches}></hl-span><strong><hl-span .text=${item.name} .hl=${suffixMatches}></hl-span></strong>
          </div>
          ${item.isPrivate ? html`<sl-tag pill size=small variant=neutral>private</sl-tag>` : nothing}
        </h-box>
      </button>
    `;
  }

  override updated(changed: PropertyValues) {
    if (changed.has('_preselectedIndex') || changed.has('_filter')) {
      const el = this.renderRoot.querySelector('.repo[preselected]') as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }

  static styles = [docLinkStyles, css`
    :host {
      display: block;
    }

    .dropdown-body {
      display: flex;
      flex-direction: column;
      max-height: 250px;
      overflow: hidden;
      background-color: white;
      padding: var(--sl-spacing-small) 0;
      border: 1px solid var(--fk-color-border);
      box-shadow: var(--fk-shadow-floating-small);
      border-radius: var(--sl-border-radius-large);

      .repolist {
        overflow: auto;
      }
    }

    sl-input {
      margin: 0 var(--sl-spacing-small);
    }

    .repolist {
      margin: 0 var(--sl-spacing-small);
      display: grid;
      gap: 0 var(--sl-spacing-small);
      grid-template-columns: auto auto 1fr;

      .repo {
        all: unset;
        cursor: pointer;
        display: grid;
        grid-column: 1/-1;
        grid-template-columns: subgrid;
        align-items: center;
        padding: var(--sl-spacing-2x-small) var(--sl-spacing-small);
        border-radius: var(--sl-border-radius-large);

        &:hover,
        &[preselected] {
          background-color: var(--sl-color-neutral-100);
        }
      }
    }

    sl-avatar {
      --size: 24px;
    }
  `];
}
